/** @format */

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import GridItem from '../../../components/Grid/GridItem';
import { useDispatch } from 'react-redux';
import { SimCardServiceTypeCollection, SimCardServiceTypeItem } from '../../../interfaces/collections/SimCardServiceInterface';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import PlatformResellerInterface from '../../../interfaces/platform/PlatformResellerInterface';
import { Card, CardContent, Checkbox, Typography } from '@material-ui/core';
import CardHeaderExt from '../../../components/Heading/CardHeaderExt';
import GridContainer from '../../../components/Grid/GridContainer';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import { SimCardServicesPartnerAllowed } from '../../../interfaces/platform/SimCardServicesPartnerAllowed';
import I18n from '../../../components/Translate/I18n';

type ComponentByGroupConfigurationType = {
    platformReseller: PlatformResellerInterface;
    groupId: number;
    groupName: string;
    partnerAllowedList: Array<{ typeId: number; enabled: number }>;
    useType: number;
};

type CheckBoxConfigurationType = {
    platformReseller: PlatformResellerInterface;
    simCardServiceType: SimCardServiceTypeItem;
    typeSelected: number;
    useType: number;
};

const CheckBoxConfiguration: FunctionComponent<CheckBoxConfigurationType> = ({
    platformReseller,
    simCardServiceType,
    typeSelected,
    useType,
}): ReactElement => {
    const dispatch = useDispatch();
    const [selected, setSelected] = useState(typeSelected);
    const [checkedManage, setCheckedManage] = useState(typeSelected === 1 || typeSelected === 3);
    const [checkedView, setCheckedView] = useState(typeSelected === 2 || typeSelected === 3);

    const getValue = (checked: boolean, type: number): number => {
        if (type === 1 && checked) {
            return selected === 2 ? 3 : 1;
        }
        if (type === 1 && !checked) {
            return selected === 3 ? 2 : -1;
        }
        if (type === 2 && checked) {
            return selected === 1 ? 3 : 2;
        }
        if (type === 2 && !checked) {
            return selected === 3 ? 1 : -1;
        }
        return -1;
    };

    const onCheckBoxEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.checked;
        const name = event.target.name;
        if (name === 'manage') {
            const v = getValue(value, 1);
            setCheckedManage(value);
            updateAllowedService(v);
            setSelected(v);
        }
        if (name === 'view') {
            const v = getValue(value, 2);
            setCheckedView(value);
            updateAllowedService(v);
            setSelected(v);
        }
    };

    const updateAllowedService = (value: number): void => {
        const itemLoader = new ItemLoaders(dispatch);
        callApi(itemLoader, {
            enabled: value,
            defaultValue: 0,
            platformResellerId: platformReseller.id,
            simCardServicesTypesId: simCardServiceType.id,
            useType: useType,
        });
    };

    const callApi = (itemLoader: ItemLoaders, params: any): void => {
        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_services_partner_alloweds')),
            'update_sim_card_services_partner_allowed_partner_' + platformReseller.id,
            params,
            (data: SimCardServicesPartnerAllowed): void => {},
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    return (
        <>
            <GridItem xs={2} style={{ textAlign: 'right' }}>
                <Checkbox checked={checkedManage} onChange={onCheckBoxEvent} name="manage" />
            </GridItem>
            <GridItem xs={2} style={{ textAlign: 'right' }}>
                <Checkbox checked={checkedView} onChange={onCheckBoxEvent} name="view" />
            </GridItem>
        </>
    );
};

export const StatusConfiguration: FunctionComponent<CheckBoxConfigurationType> = ({
    platformReseller,
    simCardServiceType,
    typeSelected,
}): ReactElement => {
    return <div>nie</div>;
};

export const ComponentByGroupConfiguration: FunctionComponent<ComponentByGroupConfigurationType> = ({
    platformReseller,
    groupId,
    groupName,
    partnerAllowedList,
    useType,
}): ReactElement => {
    const dispatch = useDispatch();
    const [groupFeaturesList, setGroupFeaturesList] = useState<Array<SimCardServiceTypeItem> | null>(null);
    const [selectedComponent, setSelectedComponent] = useState<number | null>(null);

    useEffect(() => {
        if (groupFeaturesList === null) {
            const loadItems = new ItemLoaders(dispatch);
            loadItems.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_types')),
                'group_load_features_list',
                [
                    { name: 'pagination', value: false },
                    { name: 'groupId', value: groupId },
                    { name: 'mvneOperatorId', value: useType },
                ],
                [{ name: 'description', value: 'asc' }],
                (data: SimCardServiceTypeCollection): void => {
                    setGroupFeaturesList(data['hydra:member']);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, groupFeaturesList, groupId, useType]);

    const isServiceEnabled = (typeId: number, enabledValue = 1): number => {
        for (let i = 0; i < partnerAllowedList.length; i++) {
            if (typeId === partnerAllowedList[i].typeId) {
                return partnerAllowedList[i].enabled;
            }
        }
        return -1;
    };

    return (
        <GridContainer>
            {groupFeaturesList && groupFeaturesList.length > 0 && (
                <GridItem xs={12}>
                    <Card>
                        <CardHeaderExt additionalContent={<Typography variant="h5">{groupName}</Typography>} />
                        <CardContent>
                            <GridContainer>
                                <GridItem xs={8} style={{ marginTop: '15px;' }}>
                                    <Typography variant={'subtitle2'}>
                                        <I18n trans="prov:feature.feature_name" />
                                    </Typography>
                                </GridItem>
                                <GridItem xs={2} style={{ textAlign: 'right' }}>
                                    <Typography variant={'subtitle2'}>
                                        <I18n trans="prov:feature.feature_manage" />
                                    </Typography>
                                </GridItem>
                                <GridItem xs={2} style={{ textAlign: 'right' }}>
                                    <Typography variant={'subtitle2'}>
                                        <I18n trans="prov:feature.feature_view_only" />
                                    </Typography>
                                </GridItem>
                                {groupFeaturesList.map((item) => {
                                    return (
                                        <React.Fragment key={'group_type_id' + item.id}>
                                            <GridItem xs={8}>
                                                ({item.mainname}) - {item.description}
                                            </GridItem>
                                            {UserRoleHelper.checkRole(roles.ROLE_SIM_CARD_SERVICE_PARTNER_ALLOWED_POST) && (
                                                <CheckBoxConfiguration
                                                    platformReseller={platformReseller}
                                                    simCardServiceType={item}
                                                    typeSelected={isServiceEnabled(item.id, 1)}
                                                    useType={useType}
                                                />
                                            )}
                                            {!UserRoleHelper.checkRole(roles.ROLE_SIM_CARD_SERVICE_PARTNER_ALLOWED_POST) && (
                                                <StatusConfiguration
                                                    platformReseller={platformReseller}
                                                    simCardServiceType={item}
                                                    typeSelected={isServiceEnabled(item.id)}
                                                    useType={useType}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </GridContainer>
                        </CardContent>
                    </Card>
                    <hr />
                </GridItem>
            )}
        </GridContainer>
    );
};
